/**
 * Iter-156B Phase 1 — Single source of truth for content visibility
 * labels + descriptions across Notes, Documents and Links.
 *
 * The backend tokens (everyone / owner_admin / uploader_only) are
 * unchanged from Iter-134 — only the user-facing strings are
 * canonicalised here so every surface speaks the same language.
 *
 * Naming rationale:
 *   - "Project Team" — calmer than "Everyone in project" and matches
 *     how HODs already refer to the cohort.
 *   - "Administrators Only" — production-friendly; HODs understand
 *     "Administrator" from the iter-156A.2 role model.
 *   - "Creator + Administrators" — accurate. Owner/Admin override is
 *     baked into the permission model so calling this "Private" or
 *     "Only me" would mislead users into thinking nobody else can
 *     ever see it (Administrators can, by design, to govern
 *     continuity).
 */

export const VISIBILITY_TOKENS = {
    EVERYONE: "everyone",
    OWNER_ADMIN: "owner_admin",
    UPLOADER_ONLY: "uploader_only",
};

export const VISIBILITY_LABELS = {
    everyone: "Project Team",
    owner_admin: "Administrators Only",
    uploader_only: "Restricted (Creator + Administrators)",
};

// Short pill labels — same wording, used where horizontal space
// is at a premium (row chips, filter pills).
export const VISIBILITY_PILL_LABELS = {
    everyone: "Project Team",
    owner_admin: "Admins only",
    uploader_only: "Restricted",
};

// One-sentence description shown under the picker. Plain English,
// no jargon, explains WHY the user might pick the tighter option.
export const VISIBILITY_DESCRIPTIONS = {
    everyone:
        "Visible to every member of this project. Use for continuity-relevant items.",
    owner_admin:
        "Visible only to Owner and Administrators. Use for governance or HOD-level operational items.",
    uploader_only:
        "Visible only to you and the project's Administrators. Use for personal working drafts or items that contain sensitive information (hiring, contacts, contracts).",
};

// Shared toast strings — keeps confirmation messages consistent
// across Notes and Documents.
export const VISIBILITY_CHANGE_TOASTS = {
    everyone: "Now visible to the Project Team.",
    owner_admin: "Restricted to Administrators only.",
    uploader_only: "Restricted to you and Administrators.",
};

// Calm helper sentence shown under the picker. Lifts the action
// up-front (Choose X or Y for operational documents) and tucks
// the Administrator-override reassurance at the end where it
// reads as a calm guarantee rather than a hedge.
export const VISIBILITY_HELPER =
    "Choose Administrators Only or Restricted items for operational documents that are not part of everyday continuity work. Administrators can always see all items in the project.";

// Backup-mirror policy hint shown on the connect-Dropbox card and
// near the picker. Only Project Team items leave OnSetFlow.
export const VISIBILITY_BACKUP_HINT =
    "Only Project Team items are mirrored to your external backup. Items restricted to Administrators or Creator stay inside OnSetFlow.";

export const VISIBILITY_OPTIONS = [
    {
        value: "everyone",
        label: VISIBILITY_LABELS.everyone,
        description: VISIBILITY_DESCRIPTIONS.everyone,
    },
    {
        value: "owner_admin",
        label: VISIBILITY_LABELS.owner_admin,
        description: VISIBILITY_DESCRIPTIONS.owner_admin,
    },
    {
        value: "uploader_only",
        label: VISIBILITY_LABELS.uploader_only,
        description: VISIBILITY_DESCRIPTIONS.uploader_only,
    },
];

export function visibilityLabel(token) {
    return VISIBILITY_LABELS[token] || VISIBILITY_LABELS.everyone;
}

export function visibilityPillLabel(token) {
    return VISIBILITY_PILL_LABELS[token] || VISIBILITY_PILL_LABELS.everyone;
}

export function visibilityChangeToast(token) {
    return VISIBILITY_CHANGE_TOASTS[token] || VISIBILITY_CHANGE_TOASTS.everyone;
}
